/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
// Rxjs
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
// Models
import { ApiResponse } from '@core/models';
import { ClientPlan } from '@truckstop-tms/zuum-lib';
// Services
import { ApiService } from '../../services/api.service';
import { ClientCompanyPlan } from '@new-client/models/client-company-plan.model';

@Injectable({
  providedIn: 'root',
})
export class ClientPlanService {
  private clientPlanPath: string;
  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) {
    this.clientPlanPath = `${apiService.superAdminApi}/client-plan`;
  }

  setActivePlan(clientId: number, plan: ClientCompanyPlan): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.clientPlanPath}/${clientId}`, {
        planType: plan.planType,
        billingType: plan.billingType,
        trialPeriodDays: plan.trialPeriodDays,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  updateClientPlan(id: number, payload: Partial<ClientPlan>): Observable<ApiResponse<ClientPlan>> {
    return this.http.put<ApiResponse<ClientPlan>>(`${this.clientPlanPath}/${id}`, payload);
  }

  getActivePlanFromList(clientPlans: ClientPlan[]): ClientPlan | undefined {
    try {
      return clientPlans.find((clientPlan) => {
        return (
          moment(clientPlan.startDate).isSameOrBefore() &&
          (!clientPlan.endDate || moment(clientPlan.endDate).isSameOrAfter())
        );
      });
    } catch (error) {
      console.error(error);
      return;
    }
  }

  /**
   * Normalizes plan names by replacing WaveTMS or Zuum branding with Truckstop TMS
   * @param planName - The plan name from the backend
   * @returns Normalized plan name with Truckstop TMS branding
   */
  normalizePlanName(planName: string | undefined | null): string {
    if (!planName) {
      return '';
    }
    // Replace WaveTMS with Truckstop TMS
    let normalized = planName.replace(/WaveTMS/gi, 'TruckstopTMS');
    // Replace standalone "Zuum" with "Truckstop TMS" (but preserve "Truckstop TMS" if already present)
    normalized = normalized.replace(/\bZuum\b/gi, 'Truckstop');
    // Replace "Zuum " prefix with "Truckstop TMS "
    normalized = normalized.replace(/^Zuum\s+/gi, 'Truckstop TMS ');
    return normalized;
  }

  /**
   * Normalizes add-on descriptions by replacing the letter Z and word Zuum with Truckstop TMS
   * @param description - The add-on description from the backend
   * @returns Normalized description with Truckstop TMS branding
   */
  normalizeAddOnDescription(description: string | undefined | null): string {
    if (!description) {
      return '';
    }
    let normalized = description;
    // Replace standalone word "Zuum" with "Truckstop TMS"
    normalized = normalized.replace(/\bZuum\b/gi, 'Truckstop TMS');
    // Replace "Z " (letter Z followed by space) with "Truckstop TMS "
    normalized = normalized.replace(/\bZ\s+/gi, 'Truckstop TMS ');
    // Replace "Z." (letter Z followed by period) with "Truckstop TMS."
    normalized = normalized.replace(/\bZ\./gi, 'Truckstop TMS.');
    // Replace "Z," (letter Z followed by comma) with "Truckstop TMS,"
    normalized = normalized.replace(/\bZ,/gi, 'Truckstop TMS,');
    // Replace "Z:" (letter Z followed by colon) with "Truckstop TMS:"
    normalized = normalized.replace(/\bZ:/gi, 'Truckstop TMS:');
    // Replace standalone "Z" at the end of string or before punctuation
    normalized = normalized.replace(/\bZ\b(?=[\s.,:;!?]|$)/gi, 'Truckstop TMS');
    return normalized;
  }
}
